import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Phone, Plus, Star } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import { Card, StatusBadge, LoadingPanel, EmptyPanel } from './_shared.jsx';

export default function PhoneNumbersTab() {
  const [numbers, setNumbers] = useState(null);
  const [busy, setBusy] = useState(null);
  const isAdmin = useAuth((s) => s.isAdmin?.() || s.agent?.role === 'admin' || s.agent?.role === 'superadmin');

  function load() {
    apiGet('/settings/phone-numbers').then((r) => {
      if (r?.ok) setNumbers(r.numbers || []);
    }).catch((e) => toast.error(e.message || 'Failed to load numbers'));
  }
  useEffect(load, []);

  async function setPrimary(id) {
    if (!isAdmin) { toast.error('Admins only'); return; }
    const prev = numbers;
    // Optimistic UI: flip primary locally; server is the source of truth.
    setNumbers(numbers.map((n) => ({ ...n, isPrimary: String(n._id) === String(id) })));
    setBusy(id);
    try {
      const r = await apiPost(`/settings/phone-numbers/${id}/primary`);
      if (!r?.ok) throw new Error('Failed');
      toast.success('Primary number updated');
    } catch (e) {
      setNumbers(prev);
      toast.error(e.message || 'Could not set primary');
    } finally {
      setBusy(null);
    }
  }

  if (numbers === null) return <LoadingPanel label="Loading phone numbers…" />;

  return (
    <div className="space-y-4">
      <Card
        title="Phone Numbers"
        description={isAdmin
          ? 'All active lines across this tenant. Admins can assign lines and set the default primary caller ID.'
          : 'Your personal active lines and leased numbers used for calls and SMS.'}
        actions={
          <Link
            to="/phone-setup"
            className="btn-ghost inline-flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Add via Phone Setup
          </Link>
        }
      >
        {numbers.length === 0 ? (
          <EmptyPanel
            icon={Phone}
            title="No numbers yet"
            hint={isAdmin
              ? 'No numbers found for this workspace. Use Phone Setup to provision numbers.'
              : 'You have not acquired or been assigned any phone numbers yet. Use Phone Setup to lease your personal lines.'}
            action={
              <Link to="/phone-setup" className="btn-primary inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Open Phone Setup
              </Link>
            }
          />
        ) : (
          <div className="space-y-2">
            {numbers.map((n) => (
              <div key={n._id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-civix-panelLight border border-civix-border/60">
                <div className="flex items-center gap-3 min-w-0">
                  <Phone className="w-4 h-4 text-white/40 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-white truncate">{n.phoneNumber}</div>
                    <div className="text-xs text-white/45 truncate">{n.friendlyName || '—'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={n.status || 'active'} />
                  {n.isPrimary ? (
                    <span className="pill bg-civix-purple/20 text-civix-purpleSoft border-civix-purple/40 inline-flex items-center gap-1">
                      <Star className="w-3 h-3" /> Primary
                    </span>
                  ) : isAdmin ? (
                    <button
                      onClick={() => setPrimary(n._id)}
                      disabled={busy === n._id}
                      className="btn-ghost text-xs"
                    >
                      {busy === n._id ? 'Setting…' : 'Make Primary'}
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
